import { useState, useCallback, useEffect } from "react";
import {
  Form,
  Layout,
  Card,
  Text,
  InlineCode,
  Checkbox,
  BlockStack,
  Tabs,
  LegacyCard,
  TextField,
  Button,
  List,
  DataTable,
  Box,
  DatePicker,
  Select,
  Banner,
} from "@shopify/polaris";
import { useField, useForm, notEmpty } from "@shopify/react-form";
import { useNavigate } from "react-router-dom";

import { formSubmitHandler } from "../../../utils/forms";
import { handleError } from "../../../utils/errorHandling";
import { updateSettingsV2 } from "../../../requests";
import { EVENT_TABS, FULFILLMENT_STATUS_MAPPINGS } from "./serverSideEvents";
import { CLIENT_SIDE_EVENTS } from "./clientSideEvents";
import type { EventDef, ClientSideEventDef } from "./types";

import { REQUIRED_FIELD_MESSAGE } from "../../../constants/validation";
import { FormValuesV2 } from "../utils";
import {
  settingsUpdatedMessageConfig,
  settingsErrorMessage,
} from "../../../utils/messages";

interface SettingsV2Props {
  initialValues: FormValuesV2;
  showMessage: (message: any) => void;
  setIsLoading: (isLoading: boolean) => void;
  isLoading: boolean;
  shop: string;
}

function SettingsV2Component({
  initialValues,
  showMessage,
  isLoading,
  setIsLoading,
  shop,
}: SettingsV2Props) {
  const navigate = useNavigate();

  // State for selected tab
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [{ month, year }, setDate] = useState({
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  });

  // Predefined date options
  const dateOptions = [
    { label: "", value: "none" },
    { label: "Custom date", value: "custom" },
    { label: "Last 30 days", value: "30days" },
    { label: "Last 6 months", value: "6months" },
    { label: "Last 1 year", value: "1year" },
  ];
  const [selectedDateOption, setSelectedDateOption] = useState("none");
  const handleTabChange = useCallback(
    (selectedTabIndex: number) => setSelectedTab(selectedTabIndex),
    []
  );

  // Form setup with simpler structure
  const { fields, submit, submitting, dirty } = useForm({
    fields: {
      iterableApiKey: useField({
        value: initialValues.iterableApiKey,
        validates: [notEmpty(REQUIRED_FIELD_MESSAGE)],
      }),
      webhooksEvents: useField({
        value: EVENT_TABS.reduce((acc, tab) => {
          tab.events.forEach((event) => {
            acc[event.key] =
              initialValues.webhookTopics.filter((topic) =>
                event.topics.some((eventTopic) => topic === eventTopic)
              ).length > 0;
          });
          return acc;
        }, {} as Record<string, boolean>),
        validates: [],
      }),
      clientSideEvents: useField({
        value: CLIENT_SIDE_EVENTS.reduce((acc, event) => {
          acc[event.key] = initialValues.clientSideEvents.includes(event.key);
          return acc;
        }, {} as Record<string, boolean>),
        validates: [],
      }),
      catalogResyncing: useField({
        value: initialValues.catalogResyncing,
        validates: [],
      }),
      storeType: useField({
        value: initialValues.storeType,
        validates: [],
      }),
      resyncCatalogFrom: useField({
        value: initialValues.resyncCatalogFrom,
        validates: [],
      }),
    },
    async onSubmit(formValues) {
      try {
        setIsLoading(true);
        const config = {
          request: (payload: any) => updateSettingsV2(shop, payload),
        };

        const selectedWebhookTopics = Object.entries(formValues.webhooksEvents)
          .filter(([, value]) => value)
          .flatMap(([key]) => {
            const selectedEvent = EVENT_TABS.flatMap((tab) => tab.events).find(
              (event) => event.key === key
            );

            return selectedEvent?.topics ?? [];
          });

        const payload = {
          webhook_event_topics: selectedWebhookTopics,
          custom_event_topics: Object.entries(formValues.clientSideEvents)
            .filter(([, value]) => value)
            .map(([key]) => key),
          store_type: formValues.storeType,
          is_tracking_catalog: formValues.catalogResyncing,
          resync_catalog_from:
            formValues.catalogResyncing && formValues.resyncCatalogFrom
              ? formValues.resyncCatalogFrom
              : undefined,
          // Only send API key if it changed from initial value
          ...(formValues.iterableApiKey !== initialValues.iterableApiKey && {
            iterable_api_key: formValues.iterableApiKey,
          }),
        };

        const result = await formSubmitHandler(
          navigate,
          config,
          payload,
          showMessage
        );

        if (result?.status === "success") {
          showMessage(settingsUpdatedMessageConfig);
          fields.resyncCatalogFrom.onChange("");
          const resetDate = new Date();
          setSelectedDate(resetDate);
          setDate({
            month: resetDate.getMonth(),
            year: resetDate.getFullYear(),
          });
          setSelectedDateOption("none");
          return { status: "success" };
        }

        return {
          status: "fail",
          errors: [{ message: settingsErrorMessage }],
        };
      } catch (error) {
        handleError(error, showMessage, navigate);
        return {
          status: "fail",
          errors: [{ message: settingsErrorMessage }],
        };
      } finally {
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    if (initialValues.resyncCatalogFrom) {
      const initialDate = new Date(initialValues.resyncCatalogFrom);
      if (!Number.isNaN(initialDate.getTime())) {
        setSelectedDate(initialDate);
        setDate({
          month: initialDate.getMonth(),
          year: initialDate.getFullYear(),
        });
        setSelectedDateOption("custom");
      }
      return;
    }

    setSelectedDateOption("none");
    const resetDate = new Date();
    setSelectedDate(resetDate);
    setDate({
      month: resetDate.getMonth(),
      year: resetDate.getFullYear(),
    });
  }, [
    initialValues.resyncCatalogFrom,
    setDate,
    setSelectedDate,
    setSelectedDateOption,
  ]);

  const handleDateOptionChange = (value: string) => {
    setSelectedDateOption(value);

    if (value === "none") {
      fields.resyncCatalogFrom.onChange("");
      return;
    }

    if (value === "custom") {
      fields.resyncCatalogFrom.onChange(selectedDate.toISOString());
      return;
    }

    const newDate = new Date();

    switch (value) {
      case "30days":
        newDate.setDate(newDate.getDate() - 30);
        break;
      case "6months":
        newDate.setMonth(newDate.getMonth() - 6);
        break;
      case "1year":
        newDate.setFullYear(newDate.getFullYear() - 1);
        break;
      default:
        break;
    }

    setSelectedDate(newDate);
    setDate({
      month: newDate.getMonth(),
      year: newDate.getFullYear(),
    });
    fields.resyncCatalogFrom.onChange(newDate.toISOString());
  };

  // Convert EVENT_TABS to Polaris tab format with counts
  const polarisTabDefs = EVENT_TABS.map((tab) => {
    const selectedCount = tab.events.filter(
      (event) => fields.webhooksEvents.value[event.key]
    ).length;
    const totalCount = tab.events.length;
    return {
      id: tab.id,
      content: `${tab.title} (${selectedCount}/${totalCount})`,
      accessibilityLabel: tab.title,
      panelID: tab.id,
    };
  });

  // Get current active tab
  const activeTab = EVENT_TABS[selectedTab];

  // Handle event toggle
  const handleEventToggle = useCallback(
    (eventKey: string, checked: boolean) => {
      const currentEvents = fields.webhooksEvents.value;
      fields.webhooksEvents.onChange({
        ...currentEvents,
        [eventKey]: checked,
      });
    },
    [fields.webhooksEvents]
  );

  // Handle select all events in a tab
  const handleSelectAllEvents = useCallback(
    (tabEvents: EventDef[], checked: boolean) => {
      const currentEvents = fields.webhooksEvents.value;
      const updatedEvents = { ...currentEvents };

      tabEvents.forEach((event) => {
        updatedEvents[event.key] = checked;
      });

      fields.webhooksEvents.onChange(updatedEvents);
    },
    [fields.webhooksEvents]
  );

  // Check if all events in a tab are selected
  const isAllSelected = useCallback(
    (tabEvents: EventDef[]) => {
      return tabEvents.every((event) => fields.webhooksEvents.value[event.key]);
    },
    [fields.webhooksEvents.value]
  );

  return (
    <>
      <Form noValidate preventDefault onSubmit={submit}>
        <Layout>
          <Layout.AnnotatedSection
            id="integrationConfiguration"
            title="Iterable API Key"
            description="This key will be used for server-side processing and will be used to send data to Iterable's API."
          >
            <Card>
              <BlockStack gap="400">
                <TextField
                  label="Server-side API Key"
                  {...fields.iterableApiKey}
                  autoComplete="off"
                  placeholder="Paste your Iterable server-side API key here"
                />
              </BlockStack>
            </Card>
          </Layout.AnnotatedSection>

          {initialValues.storeType === "TRADITIONAL" && (
            <Layout.AnnotatedSection
              id="clientSideEvents"
              title="Client-side events"
              description="Enable or disable client-side events that will be tracked via web pixel and sent to Iterable."
            >
              <Card>
                <BlockStack gap="400">
                  {CLIENT_SIDE_EVENTS.map((event: ClientSideEventDef) => (
                    <Checkbox
                      key={event.key}
                      label={
                        <>
                          {event.label}{" "}
                          <Text as="span" tone="subdued">
                            (<InlineCode>{event.key}</InlineCode>)
                          </Text>
                        </>
                      }
                      checked={
                        fields.clientSideEvents.value[event.key] || false
                      }
                      onChange={(checked) => {
                        const currentEvents = fields.clientSideEvents.value;
                        fields.clientSideEvents.onChange({
                          ...currentEvents,
                          [event.key]: checked,
                        });
                      }}
                      helpText={
                        <Text as="span" variant="bodySm">
                          {event.help}
                        </Text>
                      }
                    />
                  ))}
                </BlockStack>
              </Card>
            </Layout.AnnotatedSection>
          )}

          <Layout.AnnotatedSection
            id="serverSideEvents"
            title="Server-side Events"
            description="Enable or disable server-side events that will be tracked via webhooks and sent to Iterable."
          >
            <Card>
              <Tabs
                tabs={polarisTabDefs}
                selected={selectedTab}
                onSelect={handleTabChange}
              >
                <LegacyCard.Section>
                  <BlockStack gap="400">
                    <Text as="h3" variant="headingSm">
                      {activeTab.title}
                    </Text>

                    <Text as="p" tone="subdued">
                      <i>{activeTab.description}</i>
                    </Text>

                    <BlockStack gap="400">
                      <Checkbox
                        label="Select all"
                        checked={isAllSelected(activeTab.events)}
                        onChange={(checked) =>
                          handleSelectAllEvents(activeTab.events, checked)
                        }
                      />
                      <div style={{ paddingLeft: "20px" }}>
                        <BlockStack gap="400">
                          {activeTab.events.map((event: EventDef) => (
                            <Checkbox
                              key={event.key}
                              label={
                                <>
                                  {event.label}
                                  {event.iterableFieldName && (
                                    <Text as="span" tone="subdued">
                                      {" "}
                                      (
                                      <InlineCode>
                                        {event.iterableFieldName}
                                      </InlineCode>
                                      )
                                    </Text>
                                  )}
                                </>
                              }
                              checked={
                                fields.webhooksEvents.value[event.key] || false
                              }
                              onChange={(checked) =>
                                handleEventToggle(event.key, checked)
                              }
                              helpText={
                                <Text as="span" variant="bodySm">
                                  {event.help}
                                </Text>
                              }
                            />
                          ))}
                        </BlockStack>
                      </div>

                      {activeTab.id === "fulfillments" && (
                        <Box>
                          <Text as="h4" variant="headingSm">
                            Fulfillment Status Event Mappings
                          </Text>
                          <DataTable
                            columnContentTypes={["text", "text"]}
                            headings={["Fulfillment Status", "Iterable Event"]}
                            rows={FULFILLMENT_STATUS_MAPPINGS.map(
                              ({ status, event }) => [
                                <Text key={status} as="span" variant="bodySm">
                                  <InlineCode>{status}</InlineCode>
                                </Text>,
                                <Text key={event} as="span" variant="bodySm">
                                  <InlineCode>{event}</InlineCode>
                                </Text>,
                              ]
                            )}
                          />
                        </Box>
                      )}
                    </BlockStack>
                  </BlockStack>
                </LegacyCard.Section>
              </Tabs>
            </Card>
          </Layout.AnnotatedSection>

          <Layout.AnnotatedSection
            id="catalogConfiguration"
            title="Catalog Product Sync"
            description="Synchronize Shopify product data with your Iterable catalog in real time. This feature requires a Catalog subscription."
          >
            <Card>
              <BlockStack gap="400">
                <Checkbox
                  label="Enable Catalog Product Sync"
                  checked={fields.catalogResyncing.value}
                  onChange={fields.catalogResyncing.onChange}
                  disabled={!initialValues.catalogAccess}
                  helpText={
                    <>
                      Automatically sync product data, inventory, and pricing to
                      Iterable Catalogs.
                      <List type="bullet">
                        <List.Item>
                          <b>Product catalog:</b> shopName-shopifyProductCatalog
                        </List.Item>
                        <List.Item>
                          <b>Variant catalog:</b> shopifyCatalog
                        </List.Item>
                      </List>
                    </>
                  }
                />

                {fields.catalogResyncing.value && (
                  <BlockStack gap="400">
                    <Banner>
                      Select a date to backfill products. All products created
                      after the selected date will be synced to your Iterable
                      catalog.
                    </Banner>

                    <Select
                      label="Select date range"
                      options={dateOptions}
                      onChange={handleDateOptionChange}
                      onBlur={fields.resyncCatalogFrom.onBlur}
                      error={fields.resyncCatalogFrom.error}
                      value={selectedDateOption}
                    />

                    {selectedDateOption === "custom" && (
                      <DatePicker
                        month={month}
                        year={year}
                        selected={selectedDate}
                        onMonthChange={(month, year) =>
                          setDate({ month, year })
                        }
                        onChange={({ start }) => {
                          const pickedDate = start || new Date();
                          setSelectedDate(pickedDate);
                          setSelectedDateOption("custom");
                          fields.resyncCatalogFrom.onChange(
                            pickedDate.toISOString()
                          );
                        }}
                        disableDatesAfter={new Date()}
                      />
                    )}

                    {selectedDateOption !== "none" && (
                      <Text as="p" variant="bodySm">
                        Selected backfill date:{" "}
                        <Text as="span" variant="bodyMd" fontWeight="bold">
                          {selectedDate.toLocaleDateString()}
                        </Text>
                      </Text>
                    )}
                  </BlockStack>
                )}
              </BlockStack>
            </Card>
          </Layout.AnnotatedSection>

          <Layout.Section>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="primary"
                submit
                loading={submitting}
                disabled={isLoading || !dirty}
              >
                Save
              </Button>
            </div>
          </Layout.Section>
        </Layout>
      </Form>
    </>
  );
}

export default SettingsV2Component;
